import React, { useEffect, useState, useRef, useCallback } from 'react';
import { throttle } from 'lodash';
import cx from 'classnames';

import './styles/header.scss';

import Logo from '../logo/logo';
import Nav from '../navigation/nav';
import NavMobile from '../navigation/mobile-nav';
import Promotions from '../promotions/promotions';

import { t } from '../../i18n/i18n';
import headerTranslations from '../../i18n/translations/components/header/header.json';
import LanguageIconWhite from '../../images/flags/language-icon-white.png';
import LanguageIconBlack from '../../images/flags/language-icon-black.png';

import LanguageMenu from './languageMenu';
import { loginUrl } from '../../i18n/environments';
import LangA from '../../i18n/LangA';
import { useI18nInitNamespace } from '../../i18n/useI18nInitNamespace';
import CloudflareImage from '../cloudflare-image/CloudflareImage';
import { getObjectFromLocalStorage } from '../../utils/storage';
import OldPromo from '../PromoBanner/OldPromoBanner';
import { navigate } from 'gatsby';
import StickyHighlightStripAB from '../StickyStrip/stickyHighlightStripAB';

const Header = ({
  isAlt,
  isHomepage = false,
  hideNav,
  customNavElements,
  hasPromo = '',
  customPricingLink = '',
  registerUrlDestination = '',
  stickyAB = { translations: [], pageUrl: '' },
}) => {
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [latestBlogArticles, setLatestBlogArticles] = useState([]);
  const [headerStyles, setHeaderStyles] = useState({
    transform: 'translateY(0)',
    transition: 'transform .6s',
  });
  const [isJsEnabled, setIsJsEnabled] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isWhiteLanguageIcon, setIsWhiteLanguageIcon] = useState(isHomepage);
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [visible, setVisible] = useState(false);

  const headerRef = useRef(null);
  const navHolderRef = useRef(null);

  useI18nInitNamespace('header', headerTranslations, true);

  useEffect(() => {
    const membersSessionExpiresAt = getObjectFromLocalStorage('sessionExpiresAt');
    if (membersSessionExpiresAt && new Date().getTime() < membersSessionExpiresAt - 300000) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    const handleWindowSizeChange = throttle(() => {
      setIsMobile(window.innerWidth <= 1024);
    }, 200);
    if (typeof window !== 'undefined') {
      // Set initial value
      setIsMobile(window.innerWidth <= 1024);
      window.addEventListener('resize', handleWindowSizeChange, { passive: true });
      return () => window.removeEventListener('resize', handleWindowSizeChange);
    }
  }, []);

  useEffect(() => {
    const updateHeaderStyles = throttle(() => {
      if (!headerRef.current) return;
      const height = headerRef.current.offsetHeight;
      const isHidden = headerRef.current.classList.contains('is-hidden-navbar');
      const menuActive = navHolderRef.current?.classList.contains('is-active');

      let newStyles = { transform: 'none', transition: 'none' };
      if (isHidden && height) {
        newStyles = { transform: `translateY(-${height + 1}px)`, transition: 'transform .6s' };
      }
      if (menuActive) {
        newStyles = { ...newStyles, transform: 'translateY(0)' };
      }
      setHeaderStyles(newStyles);
    }, 100);

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', updateHeaderStyles, { passive: true });
      return () => window.removeEventListener('scroll', updateHeaderStyles);
    }
  }, [isMobile]);

  useEffect(() => {
    const handleScroll = throttle(() => {
      const currentScrollPos = window.pageYOffset;
      setVisible(prevScrollPos > currentScrollPos);
      setPrevScrollPos(currentScrollPos);
    }, 500);

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [prevScrollPos]);

  useEffect(() => {
    if (isHomepage) {
      setIsWhiteLanguageIcon(prevScrollPos === 0);
    } else {
      setIsWhiteLanguageIcon(false);
    }
  }, [prevScrollPos, isHomepage]);

  useEffect(() => {
    setIsJsEnabled(true);
  }, []);

  useEffect(() => {
    let initiated = false;
    const initScripts = () => {
      if (!initiated) {
        initiated = true;
        fetch('https://www.zerobounce.net/blog/wp-json/custom/v1/latest-posts?category=press')
          .then((response) => response.json())
          .then((data) => setLatestBlogArticles(data.slice(0, 2)))
          .catch(console.error);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('mousemove', initScripts, { once: true });
      window.addEventListener('touchmove', initScripts, { once: true });
      return () => {
        window.removeEventListener('mousemove', initScripts);
        window.removeEventListener('touchmove', initScripts);
      };
    }
  }, []);

  const updateMobileNavState = useCallback(() => {
    setShowMobileNav(false);
  }, []);

  return (
    <header
      style={headerStyles}
      ref={headerRef}
      className={cx('header', {
        'header--alt': isAlt,
        'is-visible': prevScrollPos !== 0,
        'is-hidden-navbar': (!visible && prevScrollPos > 0) || !isJsEnabled,
      })}
      id="header"
    >
      {hasPromo ? hasPromo === 'A' ? <OldPromo /> : null : <Promotions />}
      <div className={cx('shell-alt', { 'is-mobile': isMobile })}>
        <div className="header__inner">
          <Logo id="navigation_zb_logo" />
          <div className="nav-mobile-backdrop"></div>
          {!hideNav && (
            <div className="header__content">
              <button
                id="navigation_trigger_menu_button"
                className={cx('nav-trigger', { 'is-active': isMobile && showMobileNav })}
                onClick={() => setShowMobileNav((prev) => !prev)}
                aria-label="Open the menu"
              >
                <span>
                  <span className="seo-hidden">Trigger navigation</span>
                </span>
                <span></span>
                <span></span>
              </button>
              <div ref={navHolderRef} className={cx('nav-holder', { 'is-active': isMobile && showMobileNav })}>
                {isMobile ? (
                  <NavMobile
                    setMobileNav={updateMobileNavState}
                    customNavElements={customNavElements}
                    customPricingLink={customPricingLink}
                    handleAction={() => navigate(registerUrlDestination)}
                    registerUrlDestination={registerUrlDestination}
                  />
                ) : (
                  <Nav
                    latestBlogArticles={latestBlogArticles}
                    customNavElements={customNavElements}
                    customPricingLink={customPricingLink}
                  />
                )}
                {!isMobile && (
                  <nav className="nav-access">
                    <ul className={cx({ 'is-mobile-nav': isMobile, 'is-authenticated': isAuthenticated })}>
                      <li style={{ cursor: 'pointer' }}>
                        <CloudflareImage
                          id="navigation_language_icon"
                          src={isWhiteLanguageIcon ? LanguageIconWhite : LanguageIconBlack}
                          alt="language icon"
                          width="28"
                          height="20"
                          loading="lazy"
                          decoding="async"
                        />
                        <div className="nav__dropdown">
                          <LanguageMenu />
                        </div>
                      </li>
                      {!isAuthenticated && (
                        <li>
                          <LangA
                            id="navigation_login_button"
                            href={loginUrl}
                            rel="nofollow"
                            className={cx(
                              { 'btn btn--primary is-small text-bold': isMobile },
                              { 'text-white': isHomepage && prevScrollPos === 0 }
                            )}
                          >
                            {t('a_Log_In')}
                          </LangA>
                        </li>
                      )}
                      <li>
                        <LangA
                          id="navigation_register_dashboard_button"
                          rel="nofollow"
                          className="btn btn--primary is-small"
                          style={{ background: '#f4a41a' }}
                          href={registerUrlDestination}
                        >
                          {!isAuthenticated ? t('a_Sign_Up_Free') : t('a_Dashboard')}
                        </LangA>
                      </li>
                    </ul>
                  </nav>
                )}
              </div>
            </div>
          )}
          <noscript>
            <div className="header__content no-script-fallback">
              <nav className="nav-access">
                <ul className={cx({ 'is-mobile-nav': isMobile, 'is-authenticated': isAuthenticated })}>
                  <li style={{ cursor: 'pointer' }}>
                    <CloudflareImage
                      id="navigation_language_icon"
                      src={LanguageIconBlack}
                      alt="language icon"
                      width="28"
                      height="20"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="nav__dropdown">
                      <LanguageMenu />
                    </div>
                  </li>
                  <li>
                    <LangA
                      id="navigation_login_button"
                      href={loginUrl}
                      rel="nofollow"
                      className={cx({ 'btn btn--primary is-small text-bold': isMobile })}
                    >
                      {t('a_Log_In')}
                    </LangA>
                  </li>
                  <li>
                    <LangA
                      id="navigation_register_dashboard_button"
                      rel="nofollow"
                      href={registerUrlDestination}
                      className={cx({ 'btn btn--primary is-small': !isMobile })}
                    >
                      {t('a_Sign_Up_Free')}
                    </LangA>
                  </li>
                </ul>
              </nav>
            </div>
          </noscript>
        </div>
      </div>
      {stickyAB.pageUrl && <StickyHighlightStripAB translations={stickyAB.translations} pageUrl={stickyAB.pageUrl} />}
    </header>
  );
};

export default Header;
